import { AsyncAction } from '../types/Actions';
import { AsyncActionMethods } from '../types/ActionMethods';
import { AsyncReducerOptions } from '../types/Options';
import { AsyncState } from '../state/types';
import { AsyncStateStatus } from '../status/types';
import { createAsyncActionTypeValue } from '../actionTypes/lib';
import { createAsyncState } from '../state/lib';
import { HttpVerb } from '@sonar-software/sonar-http';
import {
	AsyncDeleteReducers,
	AsyncGetReducers,
	AsyncPatchReducers,
	AsyncPostReducers,
	AsyncPutReducers,
	AsyncReducers,
	AsyncReducerVerbOptions
} from './types';

export function createAsyncReducers<HV extends HttpVerb, N extends string, RQ = void, RC = void>(
	options: AsyncReducerOptions<HV, N>
): AsyncReducers<HV, N, RQ, RC> {
	const defaultState = createAsyncState<RC>();

	const errorTypeValue = createAsyncActionTypeValue({
		actionMethod: AsyncActionMethods.Error,
		...options
	});

	const failedTypeValue = createAsyncActionTypeValue({
		actionMethod: AsyncActionMethods.Failed,
		...options
	});

	const receiveTypeValue = createAsyncActionTypeValue({
		actionMethod: AsyncActionMethods.Receive,
		...options
	});

	const requestTypeValue = createAsyncActionTypeValue({
		actionMethod: AsyncActionMethods.Request,
		...options
	});

	const resetTypeValue = createAsyncActionTypeValue({
		actionMethod: AsyncActionMethods.Reset,
		...options
	});

	const asyncReducer = function(
		state: AsyncState<RC> = defaultState,
		action: AsyncAction<HV, N, RQ, RC>
	): AsyncState<RC> {
		const { statusCode, type } = action;

		switch (type) {
		case errorTypeValue:
			return {
				...state,
				...createAsyncState({
					actionStatus: AsyncStateStatus.Errored,
					data: undefined,
					statusCode
				})
			};
		case failedTypeValue:
			return {
				...state,
				...createAsyncState({
					actionStatus: AsyncStateStatus.Failed,
					data: undefined,
					statusCode
				})
			};
		case receiveTypeValue:
			return {
				...state,
				...createAsyncState<RC>({
					actionStatus: AsyncStateStatus.Received,
					data: action?.data ? <RC>action.data : undefined,
					statusCode
				})
			};
		case requestTypeValue:
			return {
				...state,
				...createAsyncState({
					actionStatus: AsyncStateStatus.Requested,
					data: options.retainDataOnRequest ? state.data : undefined,
					statusCode: undefined
				})
			};
		case resetTypeValue:
			return {
				...state,
				...createAsyncState({
					actionStatus: AsyncStateStatus.Idle,
					data: undefined,
					statusCode: undefined
				})
			};
		case 'reset':
			return defaultState;
		default:
			return state;
		}
	};

	return asyncReducer;
}

export function createDeleteAsyncReducers<N extends string, RQ = void, RC = void>(
	options: AsyncReducerVerbOptions<N>
): AsyncDeleteReducers<N, RQ, RC> {
	return createAsyncReducers<HttpVerb.Delete, N, RQ, RC>({
		...options,
		httpVerb: HttpVerb.Delete
	});
}

export function createGetAsyncReducers<N extends string, RQ = void, RC = void>(
	options: AsyncReducerVerbOptions<N>
): AsyncGetReducers<N, RQ, RC> {
	return createAsyncReducers<HttpVerb.Get, N, RQ, RC>({
		...options,
		httpVerb: HttpVerb.Get
	});
}

export function createPatchAsyncReducers<N extends string, RQ = void, RC = void>(
	options: AsyncReducerVerbOptions<N>
): AsyncPatchReducers<N, RQ, RC> {
	return createAsyncReducers<HttpVerb.Patch, N, RQ, RC>({
		...options,
		httpVerb: HttpVerb.Patch
	});
}

export function createPostAsyncReducers<N extends string, RQ = void, RC = void>(
	options: AsyncReducerVerbOptions<N>
): AsyncPostReducers<N, RQ, RC> {
	return createAsyncReducers<HttpVerb.Post, N, RQ, RC>({
		...options,
		httpVerb: HttpVerb.Post
	});
}

export function createPutAsyncReducers<N extends string, RQ = void, RC = void>(
	options: AsyncReducerVerbOptions<N>
): AsyncPutReducers<N, RQ, RC> {
	return createAsyncReducers<HttpVerb.Put, N, RQ, RC>({
		...options,
		httpVerb: HttpVerb.Put
	});
}
